// Sales Performance & Incentive Management — the module shell (client,
// 2026-10-06). Management sees the whole sales team; Accounts / HR read it;
// a manager sees their own team; a salesperson only themselves. The server
// decides who sees what (src/api-sales-performance).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@/shared/hooks/useUrlState';
import {
  Award, BadgeIndianRupee, BarChart3, ClipboardList, FileSpreadsheet, LayoutDashboard, ScrollText, Settings2,
  Target as TargetIcon, Trophy, Users, Wallet, ListChecks,
} from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { currentFy, inr, monthName, pctText, useOverview, type OverviewFilters } from '../api';
import { SelectPill, toneFor } from '../components/ui';
import { DashboardTab } from '../components/DashboardTab';
import { EmployeesTab, LeaderboardTab } from '../components/EmployeesTab';
import { IncentivesPanel } from '../components/IncentivesPanel';
import { RulesPanel } from '../components/RulesPanel';
import { AuditPanel } from '../components/AuditPanel';
import { TargetsTab } from '../components/TargetsTab';
import { TeamTab } from '../components/TeamTab';
import { ReportsTab } from '../components/ReportsTab';
import { WorkMonitorTab } from '../components/WorkMonitorTab';

export { monthName };

const fyOptions = () => {
  const start = Number(currentFy().slice(0, 4));
  return [start + 1, start, start - 1, start - 2].map((y) => `${y}-${String((y + 1) % 100).padStart(2, '0')}`);
};

type Tab = 'work' | 'overview' | 'employees' | 'leaderboard' | 'targets' | 'incentives' | 'team' | 'reports' | 'rules' | 'audit';

export function SalesPerformancePage() {
  const navigate = useNavigate();
  // Daily work first (client, 2026-10-28): "what is each employee doing" is
  // the question this page is opened for.
  // In the URL, so Back from an employee or record lands on the same tab (2026-10-30).
  const [tab, setTab] = useUrlState<Tab>('tab', 'work');
  const [f, setF] = useState<OverviewFilters>({ fy: currentFy(), view: 'month', metric: 'score' });
  const set = (patch: Partial<OverviewFilters>) => setF((p) => ({ ...p, ...patch }));
  const { data, isLoading, error } = useOverview(f);
  const openEmployee = (id: string) => navigate(`/sales-performance/employee/${id}?fy=${f.fy}`);
  const k = data?.kpis;
  const periodLabel = !data ? '' : data.view === 'month' ? monthName(data.month) : data.view === 'quarter' ? `${data.quarter} · FY ${data.fy}` : `Full year · FY ${data.fy}`;
  const ach = toneFor(k?.achievementPct);

  const seesAll = !!data?.access.seesAll;
  const manage = !!data?.access.manage;
  const tabs: Array<{ key: Tab; label: string; icon: typeof Trophy; show: boolean; badge?: number }> = [
    { key: 'work', label: seesAll ? 'Daily work' : 'My daily work', icon: ListChecks, show: true },
    { key: 'overview', label: 'Overview', icon: LayoutDashboard, show: true },
    { key: 'employees', label: seesAll ? 'Employees' : 'My performance', icon: Users, show: true },
    { key: 'leaderboard', label: 'Leaderboard', icon: Trophy, show: true },
    { key: 'targets', label: 'Targets', icon: TargetIcon, show: seesAll },
    { key: 'incentives', label: seesAll ? 'Incentives' : 'My incentives', icon: Wallet, show: true, badge: data?.pendingApprovals },
    { key: 'team', label: 'Team Setup', icon: Settings2, show: seesAll },
    { key: 'reports', label: 'Reports', icon: FileSpreadsheet, show: true },
    { key: 'rules', label: 'Rules', icon: ClipboardList, show: seesAll },
    { key: 'audit', label: 'Audit', icon: ScrollText, show: seesAll },
  ];

  const fo = data?.filterOptions;
  return (
    <div className="min-h-full bg-muted">
      {/* ── Header band ── */}
      <div className="bg-gradient-to-br from-[#0f2a6b] via-[#1d4ed8] to-[#2563eb] px-4 pb-14 pt-6 text-white md:px-8">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-data-xs font-semibold uppercase tracking-[0.18em] text-info-text">Sales · Performance & Incentives</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight md:text-[28px]">{seesAll ? 'Team performance' : 'My performance'}</h1>
            <p className="mt-1 text-sm text-info-text/90">{periodLabel || 'Loading…'}{data?.closedMonths.includes(data.month) && data.view === 'month' ? ' · closed & frozen' : ''}</p>
          </div>
          {k && (
            <div className="flex flex-wrap gap-3">
              <HeadStat label="Sales" value={inr(k.totalSales)} />
              <HeadStat label="Target achieved" value={pctText(k.achievementPct)} accent={k.achievementPct === null ? undefined : ach.bar} />
              <HeadStat label="Incentive" value={inr(k.incentiveEarned)} />
              {seesAll && <HeadStat label="Top performer" value={k.topPerformer?.name || '—'} />}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto -mt-9 max-w-[1500px] px-4 pb-10 md:px-8">
        {/* ── Filters ── (Daily work has its own date filter) */}
        <div className={`${tab === 'work' ? 'hidden' : 'flex'} sticky top-0 z-20 flex-wrap items-end gap-3 rounded-card border border-border bg-card/95 p-3 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] backdrop-blur`}>
          <SelectPill label="Financial year" value={f.fy} onChange={(v) => set({ fy: v, month: '' })} options={fyOptions().map((o) => ({ value: o, label: `FY ${o}` }))} />
          <label className="flex flex-col gap-1">
            <span className="text-data-xs font-semibold uppercase tracking-[0.08em] text-on-surface-subtle">View</span>
            <div className="flex rounded-lg bg-muted p-0.5">
              {(['month', 'quarter', 'year'] as const).map((v) => (
                <button key={v} type="button" onClick={() => set({ view: v })}
                  className={`rounded-md px-3 py-1 text-[13px] font-semibold capitalize transition ${f.view === v ? 'bg-card text-info-text shadow-sm' : 'text-on-surface-variant hover:text-on-surface-variant'}`}>{v}</button>
              ))}
            </div>
          </label>
          {f.view === 'month' && <SelectPill label="Month" value={data?.month || f.month || ''} onChange={(v) => set({ month: v })} options={(data?.months || []).map((m) => ({ value: m, label: monthName(m) }))} />}
          {f.view === 'quarter' && <SelectPill label="Quarter" value={data?.quarter || f.quarter || ''} onChange={(v) => set({ quarter: v })} options={['Q1', 'Q2', 'Q3', 'Q4'].map((q, i) => ({ value: q, label: `${q} · ${['Apr–Jun', 'Jul–Sep', 'Oct–Dec', 'Jan–Mar'][i]}` }))} />}
          {fo && (seesAll || fo.employees.length > 1) && (
            <>
              {seesAll && fo.departments.length > 1 && <SelectPill label="Department" value={f.department || ''} onChange={(v) => set({ department: v })} allLabel="All" options={fo.departments.map((d) => ({ value: d, label: d }))} />}
              {fo.territories.length > 0 && <SelectPill label="Territory" value={f.territory || ''} onChange={(v) => set({ territory: v })} allLabel="All" options={fo.territories.map((d) => ({ value: d, label: d }))} />}
              {fo.branches.length > 0 && <SelectPill label="Branch" value={f.branch || ''} onChange={(v) => set({ branch: v })} allLabel="All" options={fo.branches.map((d) => ({ value: d, label: d }))} />}
              {seesAll && fo.managers.length > 0 && <SelectPill label="Manager" value={f.manager || ''} onChange={(v) => set({ manager: v })} allLabel="All" options={fo.managers.map((m) => ({ value: m.id, label: m.name }))} />}
              <SelectPill label="Employee" value={f.employee || ''} onChange={(v) => set({ employee: v })} allLabel="Everyone" options={fo.employees.map((e) => ({ value: e.id, label: e.name }))} />
            </>
          )}
          {(f.department || f.territory || f.branch || f.manager || f.employee) && (
            <button type="button" onClick={() => set({ department: '', territory: '', branch: '', manager: '', employee: '' })} className="mb-0.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-info-text hover:bg-info/10">Clear filters</button>
          )}
        </div>

        {/* ── Tabs ── */}
        <nav className="mt-4 flex gap-1 overflow-x-auto rounded-card border border-border bg-card p-1.5">
          {tabs.filter((t) => t.show).map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold transition ${tab === t.key ? 'bg-blue-600 text-white shadow-sm' : 'text-on-surface-variant hover:bg-muted'}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {!!t.badge && <span className={`rounded-full px-1.5 text-data-xs ${tab === t.key ? 'bg-card/25' : 'bg-warning text-white'}`}>{t.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="mt-5">
          {tab === 'work' && <WorkMonitorTab />}
          {isLoading && tab !== 'work' && <div className="flex justify-center py-24"><Spinner /></div>}
          {error && <p className="rounded-xl bg-danger/10 p-4 text-sm text-danger-text">Could not load the performance data.</p>}
          {data && tab === 'overview' && <DashboardTab data={data} onMonth={(m) => set({ view: 'month', month: m })} onQuarter={(q) => set({ view: 'quarter', quarter: q })} onEmployee={openEmployee} onFilter={set} />}
          {data && tab === 'employees' && <EmployeesTab rows={data.rows} onOpen={openEmployee} />}
          {data && tab === 'leaderboard' && <LeaderboardTab rows={data.rows} metric={f.metric || 'score'} onMetric={(m) => set({ metric: m })} onOpen={openEmployee} seesAll={seesAll} />}
          {data && tab === 'targets' && <TargetsTab fy={f.fy} />}
          {data && tab === 'incentives' && <IncentivesPanel fy={f.fy} month={data.view === 'month' ? data.month : ''} months={data.months} closedMonths={data.closedMonths} access={data.access} />}
          {data && tab === 'team' && <TeamTab />}
          {data && tab === 'reports' && <ReportsTab filters={{ ...f, month: data.month, quarter: data.quarter }} seesAll={seesAll} />}
          {data && tab === 'rules' && <RulesPanel />}
          {data && tab === 'audit' && <AuditPanel />}
        </div>
        {manage && tab === 'overview' && <p className="mt-6 text-center text-data-xs text-on-surface-subtle">Sales are finalised tax invoices (with GST unless the rules say otherwise). New = customers added after go-live and billed for the first time that month.</p>}
      </div>
    </div>
  );
}

function HeadStat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="min-w-[8.5rem] rounded-xl bg-card/10 px-4 py-2.5 ring-1 ring-white/15 backdrop-blur">
      <p className="text-data-xs font-semibold uppercase tracking-[0.12em] text-info-text">{label}</p>
      <p className="mt-0.5 text-lg font-bold tabular-nums" style={accent ? { color: accent === '#059669' ? '#6ee7b7' : accent === '#d97706' ? '#fcd34d' : '#fda4af' } : undefined}>{value}</p>
    </div>
  );
}

// kept for other modules that imported these from here
export { Award, BadgeIndianRupee, BarChart3 };
export default SalesPerformancePage;
